import React, { useState, useEffect } from "react";
import api from "../api";
import { toast } from "sonner";
import { X, Plus, Trash2 } from "lucide-react";

const OrderForm = ({ onClose, onCreated }) => {
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    client_id: "",
    project_id: "",
    package_name: "",
    package_price: 0,
    payment_method: "transfer",
    items: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [cRes, pRes] = await Promise.all([api.get("/clients"), api.get("/projects")]);
      setClients(cRes.data);
      setProjects(pRes.data);
    } catch {
      toast.error("Blad ladowania danych");
    }
  };

  const addItem = () => {
    setForm((f) => ({
      ...f,
      items: [...f.items, { description: "", quantity: 1, unit_price: 0 }],
    }));
  };

  const updateItem = (index, field, value) => {
    setForm((f) => {
      const items = [...f.items];
      items[index] = { ...items[index], [field]: value };
      return { ...f, items };
    });
  };

  const removeItem = (index) => {
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== index) }));
  };

  const total =
    form.items.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0), 0) +
    (parseFloat(form.package_price) || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error("Wybierz klienta");
      return;
    }
    setSaving(true);
    try {
      await api.post("/orders", {
        ...form,
        package_price: parseFloat(form.package_price) || 0,
        items: form.items.map((item) => ({
          ...item,
          quantity: parseInt(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
        })),
      });
      toast.success("Zamowienie utworzone");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad tworzenia zamowienia");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Nowe zamowienie</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Klient *</label>
            <select
              value={form.client_id}
              onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))}
              required
              className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            >
              <option value="">Wybierz klienta...</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Projekt (opcjonalnie)</label>
            <select
              value={form.project_id}
              onChange={(e) => setForm((f) => ({ ...f, project_id: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            >
              <option value="">Bez projektu</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nazwa pakietu</label>
              <input
                type="text"
                value={form.package_name}
                onChange={(e) => setForm((f) => ({ ...f, package_name: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                placeholder="np. Pakiet Premium"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Cena pakietu (PLN)</label>
              <input
                type="number"
                value={form.package_price}
                onChange={(e) => setForm((f) => ({ ...f, package_price: e.target.value }))}
                min="0"
                step="0.01"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Metoda platnosci</label>
            <select
              value={form.payment_method}
              onChange={(e) => setForm((f) => ({ ...f, payment_method: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            >
              <option value="transfer">Przelew</option>
              <option value="cash">Gotowka</option>
              <option value="card">Karta</option>
              <option value="blik">BLIK</option>
            </select>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Pozycje</label>
              <button type="button" onClick={addItem} className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Dodaj
              </button>
            </div>
            {form.items.map((item, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Opis"
                  value={item.description}
                  onChange={(e) => updateItem(i, "description", e.target.value)}
                  className="flex-1 border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
                <input
                  type="number"
                  placeholder="Ilosc"
                  value={item.quantity}
                  onChange={(e) => updateItem(i, "quantity", e.target.value)}
                  min="1"
                  className="w-16 border border-slate-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
                <input
                  type="number"
                  placeholder="Cena"
                  value={item.unit_price}
                  onChange={(e) => updateItem(i, "unit_price", e.target.value)}
                  min="0"
                  step="0.01"
                  className="w-24 border border-slate-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
                <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
            <span className="font-medium text-slate-700">Suma:</span>
            <span className="text-lg font-bold text-slate-900">{total.toFixed(2)} PLN</span>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">
              Anuluj
            </button>
            <button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50">
              {saving ? "Zapisywanie..." : "Utworz zamowienie"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderForm;
